import React, {useEffect, useRef, useState} from "react";
import {
    Table,
    Button,
    message,
    Tag,
    Space,
    Popconfirm,
    Modal,
    Typography,
} from 'antd';

import api from "../api";

import {
    DeleteOutlined, SettingOutlined,
} from "@ant-design/icons";
import {Can} from "../helpers/Can";
import TimeAgo from "../helpers/TimeAgo";
import User from "../helpers/User";
import {TagImportSetup} from "./TagImports";
import {useTranslation} from "react-i18next";
import PaginationTotal from "../widgets/PaginationTotal";

export default ({newTagImport, q, onLoaded, refresh, onShowImportTags}) => {
    const {t} = useTranslation();
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({current: 1, pageSize: 10});
    const [loading, setLoading] = useState(false);

    const apiPath = '/api/tag_imports'

    const destroy = id => {
        api({
            url: `${apiPath}/${id}`,
            method: 'delete'
        }).then(res => {
            message.success(t('message-tag-import-canceled', 'Tag Import Canceled.'));
            handleTableChange();
        });
    }

    const Actions = ({tagImport}) => {
        const [visible, setVisible] = useState()

        return (<>
            <Space size={5}>
                <Can I={'edit'} a={'TagImport'}>
                    <Space direction={'horizontal'}>
                        {tagImport.aasm_state == 'analyzed' && (
                            <>
                                <Button icon={<SettingOutlined/>} onClick={() => setVisible(true)}>{t('button-process', 'Process')}... </Button>
                                <Modal
                                    title={t('tooltip-process-tag-import', 'Process Tag Import')}
                                    open={visible}
                                    onCancel={() => setVisible(false)}
                                    footer={null}
                                >
                                    <TagImportSetup
                                        tagImport={tagImport}
                                        onDone={() => {
                                            loadTableData()
                                            setVisible(false);
                                        }}
                                    />
                                </Modal>
                            </>
                        )}

                        {(tagImport.aasm_state != 'processing' && tagImport.aasm_state != 'processed') && (
                            <Popconfirm
                                title={t('confirm-cancel-tag-import', 'Cancel Tag Import?')}
                                onConfirm={() => destroy(tagImport.id)}
                            >
                                <Button size={'middle'}>
                                    <DeleteOutlined/>
                                </Button>
                            </Popconfirm>
                        )}
                    </Space>
                </Can>
            </Space>
        </>);
    }

    const columns = [
        {
            title: t('created', 'Created'),
            dataIndex: 'created_at',
            sorter: true,
            render: date => <TimeAgo date={date}/>
        },
        {
            title: t('user', 'User'),
            render: (m) => {
                return <User user={m.user}/>
            }
        },
        {
            title: t('filename', 'Filename'),
            dataIndex: 'filename',
            sorter: true,
        },
        {
            title: t('note', 'Note'),
            dataIndex: 'note',
        },
        {
            title: t('status', 'Status'),
            dataIndex: 'aasm_state',
            sorter: true,
            render: (state) => (
                <Tag>{t(state, state.toProperCase())}</Tag>
            )
        },
        {
            title: t('tags', 'Tags'),
            render: (tagImport) => {
                if (tagImport.aasm_state === 'processed') {
                    const count = tagImport.tags_count || 0;
                    if (count > 0 && onShowImportTags) {
                        return <Button type={'link'} onClick={() => onShowImportTags(tagImport.id)}>{count}</Button>
                    }
                    return <span>{count}</span>
                }
                return null
            }
        },
        {
            key: 'action',
            width: 100,
            render: (tagImport) => <Actions tagImport={tagImport}/>
        }
    ];

    const params = useRef({})

    const handleTableChange = (pagination, filters, sorter) => {
        params.current = {
            q,
            sortField: sorter?.field,
            sortOrder: sorter?.order,
            pagination,
            ...filters,
        }
        loadTableData();
    }

    const loadTableData = () => {
        setLoading(true);
        api.get(apiPath, {params: params.current}).then(res => {
            setData(res.data)
            setPagination({
                ...params.current.pagination,
                total: res.headers['total-entries']
            })
            setLoading(false)
            onLoaded && onLoaded()
        })
    }

    useEffect(handleTableChange, [newTagImport, q, refresh]);

    return (
        <>
            <Table
                bordered
                scroll={{x: true}}
                size={'small'}
                columns={columns}
                rowKey={record => record.id}
                dataSource={data}
                pagination={pagination}
                loading={loading}
                onChange={handleTableChange}
                footer={() => (
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <PaginationTotal pagination={pagination}/>
                    </div>
                )}
            />
        </>
    );
}
